import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { toE164 } from "../lib/phone";
import { passwordProblem } from "../lib/password";
import { useAuth } from "../context/AuthContext";
import AuthLayout from "../components/auth/AuthLayout";
import { PhoneField } from "../components/auth/ContactFields";
import {
  dividerLineStyle,
  dividerStyle,
  errorStyle,
  fieldStyle,
  finePrintStyle,
  formStyle,
  googleButtonStyle,
  inputStyle,
  labelRowStyle,
  labelStyle,
  linkButtonStyle,
  noticeStyle,
  primaryButtonStyle,
  subtitleStyle,
  titleStyle,
  colors,
} from "../components/auth/authStyles";

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
    <path
      fill="#FFC107"
      d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"
    />
    <path
      fill="#FF3D00"
      d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"
    />
    <path
      fill="#4CAF50"
      d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"
    />
    <path
      fill="#1976D2"
      d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"
    />
  </svg>
);

const ArrowIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

const EyeIcon = ({ open }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#888888"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" />
    <circle cx="12" cy="12" r="3" />
    {!open && <path d="M4 4l16 16" />}
  </svg>
);

const COPY = {
  signin: {
    title: "Sign in",
    prompt: "Don't have an account?",
    switchLabel: "Create an account",
    submit: "Sign in",
    body: "Sign in to buy balls and shoes from the shop, track your orders, and keep your specs on file for the next drill.",
  },
  signup: {
    title: "Create your account",
    prompt: "Already have one?",
    switchLabel: "Sign in",
    submit: "Create account",
    body: "One account for everything you buy from the shop: orders, receipts, and pickup updates, all in one place.",
  },
  forgot: {
    title: "Reset your password",
    prompt: "Remembered it?",
    switchLabel: "Back to sign in",
    submit: "Send reset link",
    body: "We'll email you a link to set a new password.",
  },
};

const Login = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from || "/account";

  const [mode, setMode] = useState(
    location.pathname === "/signup" ? "signup" : "signin"
  );
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  // Coming back from sign-up: prefill the new account's email.
  const createdEmail = location.state?.createdEmail;
  const [email, setEmail] = useState(createdEmail || "");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState(
    createdEmail ? "Account created. Sign in with your new password." : ""
  );
  // True while email sign-up runs, so the new session doesn't trigger the
  // signed-in redirect below before we can send them to sign in.
  const signingUp = useRef(false);

  // Already signed in (including right after sign-in succeeds): move on.
  useEffect(() => {
    if (user && !signingUp.current) navigate(redirectTo, { replace: true });
  }, [user, navigate, redirectTo]);

  const switchMode = (next) => {
    // Sign in and sign up have their own URLs so refresh, back, and shared
    // links land on the right form. "Forgot password" stays on /login.
    const path = next === "signup" ? "/signup" : next === "signin" ? "/login" : null;
    if (path && path !== location.pathname) {
      navigate(path, { state: location.state });
      return;
    }
    setMode(next);
    setError("");
    setNotice("");
  };

  const handleGoogle = async () => {
    setError("");
    const { error: oauthError } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: `${window.location.origin}${redirectTo}` },
    });
    if (oauthError) setError(oauthError.message);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setNotice("");

    let phoneE164 = null;
    if (mode === "signup") {
      phoneE164 = toE164(phone);
      if (!phoneE164) {
        setError("Please enter a 10-digit mobile number, like (647) 555-0147.");
        return;
      }
      const problem = passwordProblem(password, confirmPassword);
      if (problem) {
        setError(problem);
        return;
      }
    }

    setSubmitting(true);
    try {
      if (mode === "signin") {
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (signInError) throw signInError;
      } else if (mode === "signup") {
        signingUp.current = true;
        const { data, error: signUpError } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/account`,
            // Copied into public.profiles by the handle_new_user() trigger.
            // No contact_pref for now: the trigger defaults it to "text".
            data: {
              full_name: `${firstName.trim()} ${lastName.trim()}`.trim(),
              phone: phoneE164,
            },
          },
        });
        if (signUpError) throw signUpError;
        // Supabase hides whether an email is taken by returning a user with
        // no identities instead of an error.
        if (data.user && data.user.identities?.length === 0) {
          throw new Error(
            "An account with this email already exists. Try signing in instead."
          );
        }
        if (data.session) {
          // Email confirmation is off, so Supabase signed them straight in.
          // Sign out and send them to sign in with the new account instead.
          await supabase.auth.signOut({ scope: "local" });
          navigate("/login", {
            replace: true,
            state: { ...location.state, createdEmail: email },
          });
          return;
        } else {
          // Only happens if "Confirm email" is turned on in Supabase.
          signingUp.current = false;
          setNotice(
            `Almost done. We sent a confirmation link to ${email}. Click it to finish creating your account.`
          );
          setPassword("");
          setConfirmPassword("");
        }
      } else {
        const { error: resetError } =
          await supabase.auth.resetPasswordForEmail(email, {
            redirectTo: `${window.location.origin}/reset-password`,
          });
        if (resetError) throw resetError;
        setNotice(
          `If there's an account for ${email}, a password reset link is on its way.`
        );
      }
    } catch (err) {
      signingUp.current = false;
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const copy = COPY[mode];

  const emailField = (
    <div style={fieldStyle}>
      <label htmlFor="email" style={labelStyle}>
        Email
      </label>
      <input
        id="email"
        type="email"
        autoComplete="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="auth-input"
        style={inputStyle}
      />
    </div>
  );

  const passwordField = (
    <div style={fieldStyle}>
      <div style={labelRowStyle}>
        <label htmlFor="password" style={labelStyle}>
          Password
        </label>
        {mode === "signin" ? (
          <button
            type="button"
            style={{ ...linkButtonStyle, fontSize: "0.875rem" }}
            onClick={() => switchMode("forgot")}
          >
            Forgot password?
          </button>
        ) : null}
      </div>
      <div style={{ position: "relative" }}>
        <input
          id="password"
          type={showPassword ? "text" : "password"}
          autoComplete={
            mode === "signup" ? "new-password" : "current-password"
          }
          required
          minLength={mode === "signup" ? 8 : undefined}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="auth-input"
          style={{ ...inputStyle, paddingRight: "3rem" }}
        />
        <button
          type="button"
          onClick={() => setShowPassword((v) => !v)}
          aria-label={showPassword ? "Hide password" : "Show password"}
          style={{
            position: "absolute",
            right: "0.5rem",
            top: "50%",
            transform: "translateY(-50%)",
            display: "flex",
            padding: "0.5rem",
            background: "none",
            border: "none",
            cursor: "pointer",
          }}
        >
          <EyeIcon open={showPassword} />
        </button>
      </div>
    </div>
  );

  const confirmPasswordField = (
    <div style={fieldStyle}>
      <label htmlFor="confirm-password" style={labelStyle}>
        Confirm password
      </label>
      {/* Follows the eye toggle on the password field next to it. */}
      <input
        id="confirm-password"
        type={showPassword ? "text" : "password"}
        autoComplete="new-password"
        placeholder="Re-enter your password"
        required
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        className="auth-input"
        style={inputStyle}
      />
    </div>
  );

  const nameField = (id, label, value, onChange, autoComplete) => (
    <div style={fieldStyle}>
      <label htmlFor={id} style={labelStyle}>
        {label}
      </label>
      <input
        id={id}
        type="text"
        autoComplete={autoComplete}
        required
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="auth-input"
        style={inputStyle}
      />
    </div>
  );

  const switchLine = (
    <p
      style={
        mode === "forgot"
          ? subtitleStyle
          : { ...subtitleStyle, margin: 0, textAlign: "center" }
      }
    >
      {copy.prompt}{" "}
      <button
        type="button"
        style={linkButtonStyle}
        onClick={() => switchMode(mode === "signin" ? "signup" : "signin")}
      >
        {copy.switchLabel}
      </button>
    </p>
  );

  const googleButton = (
    <button type="button" style={googleButtonStyle} onClick={handleGoogle}>
      <GoogleIcon />
      {mode === "signup" ? "Sign up with Google" : "Continue with Google"}
    </button>
  );

  const divider = (label) => (
    <div style={dividerStyle}>
      <span style={dividerLineStyle} />
      {label}
      <span style={dividerLineStyle} />
    </div>
  );

  return (
    <AuthLayout body={copy.body}>
      <form
        style={{ ...formStyle, maxWidth: mode === "signup" ? "520px" : "400px" }}
        onSubmit={handleSubmit}
      >
        <div>
          <h1 style={titleStyle}>{copy.title}</h1>
          {mode === "forgot" && switchLine}
        </div>

        {/* On sign-up Google goes first: it skips most of the form. */}
        {mode === "signup" && (
          <>
            {googleButton}
            {divider("OR SIGN UP WITH EMAIL")}
          </>
        )}

        {mode === "signup" ? (
          // Paired fields sit side by side and wrap to one column when the
          // form gets narrow (see .auth-row in index.css).
          <div style={{ display: "flex", flexDirection: "column", gap: "1.1rem" }}>
            <div className="auth-row">
              {nameField("first-name", "First name", firstName, setFirstName, "given-name")}
              {nameField("last-name", "Last name", lastName, setLastName, "family-name")}
            </div>
            <div className="auth-row">
              {emailField}
              <PhoneField value={phone} onChange={setPhone} />
            </div>
            <div className="auth-row">
              {passwordField}
              {confirmPasswordField}
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "1.1rem" }}>
            {emailField}
            {mode !== "forgot" && passwordField}
          </div>
        )}

        {error && (
          <div style={errorStyle} role="alert">
            {error}
          </div>
        )}
        {notice && (
          <div style={noticeStyle} role="status">
            {notice}
          </div>
        )}

        <button
          type="submit"
          style={{ ...primaryButtonStyle, opacity: submitting ? 0.7 : 1 }}
          disabled={submitting}
        >
          {submitting ? "Please wait..." : copy.submit}
          {!submitting && <ArrowIcon />}
        </button>

        {mode === "signup" && (
          <p style={finePrintStyle}>
            We only use your number to reach you about your orders.
          </p>
        )}

        {mode === "signin" && (
          <>
            {divider("OR")}
            {googleButton}
          </>
        )}
        {mode !== "forgot" && switchLine}
        {mode === "forgot" && (
          <p style={{ ...finePrintStyle, color: colors.muted }}>
            Signed up with Google? Use "Continue with Google" instead; there's
            no password to reset.
          </p>
        )}
      </form>
    </AuthLayout>
  );
};

export default Login;
